/****************************************************
 * Module Name:  polMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import PolProxy from "../../utility/proxy/manager/polProxy";
import AddPolModal from "./addPolModal";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class PolMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pols: [],
      showAddPolModal: false,
      loadingButton: false,
      editOperation: false,
      sourceData: {},
      errorSnackbar: false,
      accesDenied: false,
      showPolLoadSpinner: true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MASTER_POL
      ),
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("MASTER_POL");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchPolData(false);
    }
  }

  fetchPolData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showPolLoadSpinner: true });
    PolProxy.GetAllPol((responseData) => {
      if (responseData) {
        this.setState({ pols: responseData, showPolLoadSpinner: false })
      } else {
        this.setState({ errorSnackbar: true, showPolLoadSpinner: false })
      }
    }
    );
  };

  columns = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      type: 'number',
      align: "center",
    },
    {
      field: "name",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "fullname",
      headerName: "FULLNAME",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.label),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "description",
      headerName: "DESCRIP",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.label),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "seq",
      headerName: "SEQUENCE",
      headerAlign: "center",
      align: "center",
      type: 'number',
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => {
              this.setState({
                showAddPolModal: true,
                editOperation: true,
                sourceData: params.row,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddPolModal = () => {
    this.setState({
      showAddPolModal: false,
    });
  };

  handleValidation = (polData) => {
    //TODO do validation if needed here
    return true;
  };

  addPol = (polData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      //Updating Pol, and once pol is updated on call refresh the list of current pols
      PolProxy.UpdatePol(polData, (response) => {
        if (response) {
          this.fetchPolData(true);
          this.setState({ loadingButton: false });
          //Hidding pol modal
          this.hideAddPolModal();
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    } else {
      //Creating new Pol, and once pol is created on call refresh the list of current pols
      PolProxy.AddPol(polData, (response) => {
        if (response) {
          this.fetchPolData(true);
          this.setState({ loadingButton: false });
          //Hidding pol modal
          this.hideAddPolModal();
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    }
  };

  render() {
    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={{
            display: "flex",
            alignItems: "center",
            marginHorizontal: "5px",
            "&.MuiDataGrid-toolbarContainer": {
              padding: "0px 4px !important",
            },
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
          <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
          {this.state.showPolLoadSpinner ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlinedIcon
                color="primary"
                fontSize="medium"
                onClick={() => this.fetchPolData(true)}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          )}
          {Utility.isAddAllowed(this.state.local_Permission) ? (
            <Tooltip title="Add Pol">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                onClick={() =>
                  this.setState({
                    showAddPolModal: true,
                    editOperation: false,
                    sourceData: {},
                  })
                }
                startIcon={<AddBoxOutlinedIcon size="large" />}
              >
                Pol
              </Button>
            </Tooltip>
          ) : null}
          <GridToolbarQuickFilter debounceMs={500} />
        </GridToolbarContainer>
      );
    }
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.pols;
    const columns = Utility.isUpdateAllowed(this.state.local_Permission)
      ? this.columns
      : this.columns.filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{Constants.APiFailErrorMessage}</Box>}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title="Failed"
        />
        <br />
        {this.state.showAddPolModal ? (
          <AddPolModal
            Show={true}
            handleClose={this.hideAddPolModal}
            handleSave={this.addPol}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "85vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },
                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              density="compact"
              loading={this.state.showPolLoadSpinner}
              slots={{ toolbar: CustomToolbar }}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    id: false,
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default PolMaster;
